/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

// type VisitorType = 'sme' | 'eu';
import { Procedure } from '../generated/graphql';

// TODO: Utilize this enum instead of hard-coded strings in the project
export enum DepartmentSubmodules {
  DEPARTMENTS = 'departments',
  PLAYGROUND = 'playground',
  LEARNINGS = 'learnings',
  HISTORY = 'history',
  COLLABORATORS = 'collaborators',
  DASHBOARD = 'dashboard',
  EXCEPTIONS = 'exceptions',
  BOOKS = 'books',
  PROCESSES = 'processes'
}

export const DEPARTMENT_SUBMODULE_LIST = Object.values(DepartmentSubmodules);

// Auth
const getLoginPageUrl = () => '/login';

const getVerifyLoginPageUrl = (challenge: string) => `verify-user/${challenge}`;

// Global
const getHomePageUrl = () => '/';

const getPlaygroundPageUrl = (departmentId: string, workerId?: string) =>
  `/department/${departmentId}/playground${workerId ? `/${workerId}` : ''}`;

// Procedure
const getProcedureHomePageUrl = (departmentId: string) =>
  `/department/${departmentId}/processes`;

const getProceduresPublishUrl = (departmentId: string) =>
  `/department/${departmentId}/processes/publish`;

const getProceduresReviewPageUrl = (departmentId: string) =>
  `/department/${departmentId}/processes/publish/review`;

const getProcedureReviewPageUrl = (departmentId: string, procedureId: string) =>
  `${getProceduresReviewPageUrl(departmentId)}/${procedureId}`;

const getStageParams = (stage?: string) => (stage ? `?stage=${stage}` : '');

const getProcedurePageUrl = (
  departmentId: Procedure['departmentId'],
  procedureId: string,
  stage?: string
) => {
  return `/department/${departmentId}/processes/${procedureId}${getStageParams(
    stage
  )}`;
};

const getProcedureRunAllPageUrl = (
  departmentId: Procedure['departmentId'],
  procedureId: string,
  stage?: string
) =>
  `${getProcedurePageUrl(departmentId, procedureId)}/run/all${getStageParams(
    stage
  )}`;

const getProcedureRunPageUrl = (
  departmentId: Procedure['departmentId'],
  procedureId: string,
  workerId: string
) => `${getProcedurePageUrl(departmentId, procedureId)}/run/${workerId}`;

const getProcedureLearningsPageUrl = (
  departmentId: Procedure['departmentId'],
  procedureId: string,
  stage?: string
) =>
  `${getProcedurePageUrl(departmentId, procedureId)}/learnings${getStageParams(
    stage
  )}`;

const getProcedureSchedulesPageUrl = (
  departmentId: Procedure['departmentId'],
  procedureId: string,
  stage?: string
) =>
  `${getProcedurePageUrl(departmentId, procedureId)}/schedules${getStageParams(
    stage
  )}`;

const getProcedureOutputPageUrl = (
  departmentId: Procedure['departmentId'],
  procedureId: string,
  stage?: string
) =>
  `${getProcedurePageUrl(departmentId, procedureId)}/outputs${getStageParams(
    stage
  )}`;

const getProcedureExceptionsPageUrl = (
  departmentId: Procedure['departmentId'],
  procedureId: string,
  stage?: string
) =>
  `${getProcedurePageUrl(departmentId, procedureId)}/exceptions${getStageParams(
    stage
  )}`;

const getNewPythonProcessPageUrl = (departmentId: string) =>
  `/department/${departmentId}/process/new/python`;

// Department
const getDepartmentsPageUrl = (departmentId: string) =>
  `/department/${departmentId}/departments`;
const getDepartmentLearningsPageUrl = (departmentId: string) =>
  `/department/${departmentId}/learnings`;
const getDepartmentHistoryPageUrl = (departmentId: string) =>
  `/department/${departmentId}/history`;
const getDepartmentCollaboratorsPageUrl = (departmentId: string) =>
  `/department/${departmentId}/collaborators`;

// Admin
const getAdminPageUrl = () => `/internal/admin`;
const getAdminInsightsPageUrl = () => `/internal/insights`;

// Dashboard
const getDashboardPageUrl = (departmentId: string) =>
  `/department/${departmentId}/dashboard`;

// Exceptions
const getExceptionsPageUrl = (departmentId: string) =>
  `/department/${departmentId}/exceptions`;

const getExceptionPageUrl = (departmentId: string, exceptionId: string) =>
  `${getExceptionsPageUrl(departmentId)}/${exceptionId}`;

// Books
const getBooksPageUrl = (departmentId: string) =>
  `/department/${departmentId}/books`;

const authRoutes = {
  getLoginPageUrl,
  getVerifyLoginPageUrl
};

const globalRoutes = {
  getHomePageUrl
};

const playgroundRoutes = {
  getPlaygroundPageUrl
};

const procedureRoutes = {
  getProcedureHomePageUrl,
  getProcedurePageUrl,
  getProcedureRunAllPageUrl,
  getProcedureRunPageUrl,
  getProcedureLearningsPageUrl,
  getProcedureSchedulesPageUrl,
  getNewPythonProcessPageUrl,
  getProceduresPublishUrl,
  getProceduresReviewPageUrl,
  getProcedureReviewPageUrl,
  getProcedureOutputPageUrl,
  getProcedureExceptionsPageUrl
};

const departmentRoutes = {
  getDepartmentsPageUrl,
  getDepartmentLearningsPageUrl,
  getDepartmentHistoryPageUrl,
  getDepartmentCollaboratorsPageUrl
};

const booksRoutes = {
  getBooksPageUrl
};

const adminRoutes = {
  getAdminPageUrl,
  getAdminInsightsPageUrl
};

const dashboardRoutes = {
  getDashboardPageUrl
};

const exceptions = {
  getExceptionsPageUrl,
  getExceptionPageUrl
};

const routes = {
  auth: authRoutes,
  global: globalRoutes,
  playground: playgroundRoutes,
  procedure: procedureRoutes,
  department: departmentRoutes,
  admin: adminRoutes,
  dashboard: dashboardRoutes,
  exceptions,
  books: booksRoutes
};

export default routes;
